import React, { useRef, useEffect } from 'react';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";

interface SignedOutPopupProps {}

const SignedOutPopup: React.FC<SignedOutPopupProps> = () => {
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const modalRef = useRef<HTMLDivElement>(null);

    const handleClose = () => {
        setDynamicStateObj({ visiblePopup: null });
    };

    const handleSignIn = () => {
        setDynamicStateObj({ visiblePopup: "signin" });
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
            handleClose();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50">
            <div 
                ref={modalRef} 
                className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-[98%] sm:w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 max-w-lg overflow-hidden border border-neutral-300/50 dark:border-neutral-700/50 relative"
            >
                {/* Gradient bar at top */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-600"></div>
                
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                    aria-label="Close popup"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="p-8 text-center">
                    {/* Icon */}
                    <div className="mb-6">
                        <div className="w-16 h-16 mx-auto bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                    </div>

                    {/* Header */}
                    <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                        You're signed out
                    </h2>

                    {/* Message */}
                    <p className="text-neutral-600 dark:text-neutral-300 mb-8 leading-relaxed">
                        Sign in to unlock access to more features and get the most out of your Onuro experience.
                    </p>

                    {/* Features preview */}
                    <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-6 mb-8 text-left">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4 text-center">
                            What you'll get:
                        </h3>
                        <ul className="space-y-3">
                            <li className="flex items-center">
                                <span className="text-green-500 mr-3 text-sm">✓</span>
                                <span className="text-neutral-600 dark:text-neutral-300 text-sm">
                                    Save your chat history
                                </span>
                            </li>
                            <li className="flex items-center">
                                <span className="text-green-500 mr-3 text-sm">✓</span>
                                <span className="text-neutral-600 dark:text-neutral-300 text-sm">
                                    Limited full feature access (first time users)
                                </span>
                            </li>
                            <li className="flex items-center">
                                <span className="text-green-500 mr-3 text-sm">✓</span>
                                <span className="text-neutral-600 dark:text-neutral-300 text-sm">
                                    Personalized settings & preferences
                                </span>
                            </li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={handleSignIn}
                            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center"
                            aria-label="Sign in to access more features"
                        >
                            Sign In
                        </button>
                        <button
                            onClick={handleClose}
                            className="px-6 py-3 bg-neutral-200 dark:bg-neutral-600 hover:bg-neutral-300 dark:hover:bg-neutral-500 text-neutral-700 dark:text-neutral-200 rounded-lg font-semibold transition-colors"
                            aria-label="Continue without signing in"
                        >
                            Continue Without Account
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignedOutPopup;